import type { Dispatch, SetStateAction } from 'react'
import type { components } from '@joyful/openapi/types.gen'
import { useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { createEnumSchema } from '@joyful/utils'
import { LeftCornerPositionSchema } from '../../../../core'
import { CurrentUserContext } from '../../../utils/currentUserContext'
import { buildApiQueryKey, useApiMutation } from '../../../utils/reactQuery'

export const DemoStreamSpeedSchema = createEnumSchema<
  components['schemas']['DemoStreamSpeed']
>([
  { title: 'Slow', value: 'slow' },
  { title: 'Default', value: 'default' },
  { title: 'Fast', value: 'fast' },
])

const DemoModeSettingsSchema = z.object({
  account_slug: z
    .string()
    .nullable()
    .default(null)
    .describe('When set, demo mode is active and scoped to this account slug.'),
  stream_speed: DemoStreamSpeedSchema.schema
    .default('default')
    .describe('Replay cadence for the encounter agent demo replay.'),
})

export const DisplaySettingsSchema = z.object({
  demo_mode: DemoModeSettingsSchema.default({
    account_slug: DemoModeSettingsSchema.shape.account_slug.def.defaultValue,
    stream_speed: DemoModeSettingsSchema.shape.stream_speed.def.defaultValue,
  }),
  floating_list_navigation_panel_corner: LeftCornerPositionSchema.schema
    .default('bottom-left')
    .describe('Floating list navigation panel position'),
})

type DisplaySettings = components['schemas']['UserDisplaySettings']

const DEFAULT_DISPLAY_SETTINGS: DisplaySettings = {
  demo_mode: undefined,
  floating_list_navigation_panel_corner: 'bottom-left',
}

type CurrentUserRead = components['schemas']['CurrentUserRead']

const currentMeKey = buildApiQueryKey('/users/me')

export function useDisplaySettings(): [
  DisplaySettings,
  Dispatch<SetStateAction<DisplaySettings>>,
] {
  const currentUser = CurrentUserContext.useSafeContext()
  const queryClient = useQueryClient()
  const mutation = useApiMutation('put', '/users/me/display-settings')

  const displaySettings =
    currentUser?.display_settings ?? DEFAULT_DISPLAY_SETTINGS

  const setDisplaySettings: Dispatch<SetStateAction<DisplaySettings>> = (
    nextOrUpdater,
  ) => {
    if (currentUser === null) {
      return
    }

    const cachedMeQueries = queryClient.getQueriesData<CurrentUserRead>({
      queryKey: currentMeKey,
    })
    const latest =
      cachedMeQueries.at(0)?.[1]?.display_settings ?? displaySettings

    const next =
      typeof nextOrUpdater === 'function'
        ? nextOrUpdater(latest)
        : nextOrUpdater

    queryClient.setQueriesData<CurrentUserRead>(
      { queryKey: currentMeKey },
      (oldData) => {
        if (oldData === undefined) {
          return oldData
        }

        return { ...oldData, display_settings: next }
      },
    )

    mutation.mutate(
      { body: next },
      {
        onError: () => {
          void queryClient.invalidateQueries({ queryKey: currentMeKey })
        },
        onSettled: () => {
          void queryClient.invalidateQueries({ queryKey: currentMeKey })
        },
      },
    )
  }

  return [displaySettings, setDisplaySettings]
}
